<template>
  <div class="phoneInput">
    <label :for="`dynamicInput-${inputData.id}`" v-html="addNbsp(getLocaleText(inputData.label))" />
    <v-row no-gutters>
      <v-col class="flex-grow-0">
        <UlSelect
          v-model="phoneCountry"
          data-cy="UlPhoneSelectCountry"
          :menu-props="{ contentClass: 'my-1', minWidth: '160px' }"
          :options="countries"
          :tabindex="-1"
          item-text="code"
          item-value="code"
          return-object
          @change="updateValue()"
        >
          <template #optionPrepend="{ item }">
            <v-list-item-icon class="ma-0">
              <v-img
                :src="$asset(`img/countryFlags/${getFlagImage(item)}.svg`)"
                class="mr-1"
                contain
                max-width="16px"
              />
            </v-list-item-icon>
          </template>
          <template #optionTitle="{ item }">
            {{ item.prefix }}
          </template>
          <template #selection="{ item }">
            <v-img
              :src="$asset(`img/countryFlags/${getFlagImage(item)}.svg`)"
              class="mr-1"
              contain
              max-width="16px"
            />
            {{ item.prefix }}
          </template>
        </UlSelect>
      </v-col>
      <v-col class="pr-0 pt-sm-0 pl-2">
        <v-text-field
          id="phone"
          v-model="$v.phoneNumber.$model"
          data-cy="UlPhoneTextField"
          :error="!!phoneErrorMessage.length"
          :class="{
            'is-valid':
              $v.phoneNumber.$dirty &&
              !$v.phoneNumber.$invalid &&
              ((!inputData.required && phoneNumber.length) || inputData.required)
          }"
          :placeholder="placeholderText"
          autocomplete="off"
          flat
          hide-details
          solo
          @focus="handleFocus"
          @change="updateValue()"
          @keydown="$v.phoneNumber.$touch()"
        >
          <template #append>
            <v-icon
              v-if="
                $v.phoneNumber.$dirty &&
                $v.phoneNumber.$error === false &&
                ((!inputData.required && phoneNumber.length) || inputData.required)
              "
            >
              $tick
            </v-icon>
            <v-icon v-if="$v.phoneNumber.$dirty && $v.phoneNumber.$invalid === true">
              $warning
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <div class="v-text-field__details mt-2">
      <div
        class="v-messages theme--light"
        :class="{ 'error--text': !!phoneErrorMessage.length }"
        role="alert"
      >
        <div class="v-messages__wrapper">
          <div class="v-messages__message">
            {{ !!phoneErrorMessage.length ? phoneErrorMessage : message }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formGtm from '@/mixins/formGtm';
import { required } from 'vuelidate/lib/validators';
import { phone as phoneValidator } from '@/customVuelidate';
import { countryCallingCodes } from '@/assets/js/country-codes';

function normalize(prefix, local) {
  const pref = (prefix || '').toString().trim();
  let normal = (local || '').toString().replace(/\s+/g, '');
  if (!normal) {
    return '';
  }
  if (normal.startsWith('+')) {
    return normal;
  }
  if (normal.startsWith('00')) {
    normal = '+' + normal.slice(2);
  }
  if (/^\d+$/.test(normal)) {
    return (pref || '') + normal;
  }
  return normal;
}

export default {
  name: 'UlPhone',
  mixins: [formGtm],
  props: {
    inputData: { type: Object, required: true },
    stepId: { type: Number, required: true },
    // NEW: keep contract with new engine + legacy screens
    value: { required: false, default: '' },
    formName: { type: String, required: false, default: null }
  },
  data() {
    return {
      phoneNumber: '',
      phoneCountrySelected: null
    };
  },
  validations() {
    if (this.disabled) {
      return {};
    }
    const pref = this.phoneCountry?.prefix || '';
    if (this.inputData.required) {
      return { phoneNumber: { required, phone: phoneValidator(pref) } };
    }
    return this.phoneNumber.length
      ? { phoneNumber: { phone: phoneValidator(pref) } }
      : { phoneNumber: {} };
  },
  computed: {
    responses() {
      return this.$store.getters[`dynamicForms/responses`];
    },
    placeholderText() {
      return this.getLocaleText(this.inputData.placeholder) || this.phoneExample;
    },
    message() {
      return this.getLocaleText(this.inputData.help) || '';
    },
    phoneErrorMessage() {
      if (!this.$v.$dirty || (!this.inputData.required && !this.phoneNumber.length)) {
        return '';
      }
      if (this.inputData.required && !this.phoneNumber.length) {
        return this.$t('questionForm.phone.errorText');
      }
      if (this.$v.phoneNumber.$error) {
        return this.$t('questionForm.phone.errorPhoneText', { exampleNumber: this.phoneExample });
      }
      return '';
    },
    phoneExample() {
      return this.phoneCountry?.example || '';
    },
    countries() {
      return countryCallingCodes;
    },
    phoneCountry: {
      get() {
        if (this.phoneCountrySelected !== null) {
          return this.phoneCountrySelected;
        }
        const regionDefault = {
          cz: { code: 'CZ', prefix: '+420', example: '123 456 789' },
          sk: { code: 'SK', prefix: '+421', example: '123 456 789' },
          hu: { code: 'HU', prefix: '+36', example: '06 1 234 5678' }
        }[this.$domainRegion] ||
          this.countries?.[0] || { code: 'CZ', prefix: '+420', example: '123 456 789' };
        return regionDefault;
      },
      set(val) {
        this.phoneCountrySelected = val;
      }
    }
  },
  watch: {
    // NEW: keep in sync with parent prop (no emit on mount)
    value: {
      immediate: true,
      handler(val) {
        const next = val === null ? '' : String(val);
        // if parent handed full E.164, split to prefix+local for UI
        const match = next.match(/^\+(\d{1,3})(\d+)$/);
        if (match) {
          const pref = '+' + match[1];
          const local = match[2];
          const found = this.countries.find(country => country.prefix === pref);
          if (found) {
            this.phoneCountry = found;
          }
          if (this.$v && this.$v.phoneNumber && this.$v.phoneNumber.$model !== local) {
            this.$v.phoneNumber.$model = local;
          } else {
            this.phoneNumber = local;
          }
          return;
        }
        // else treat as local part, keep current prefix
        if (this.$v && this.$v.phoneNumber && this.$v.phoneNumber.$model !== next) {
          this.$v.phoneNumber.$model = next;
        } else {
          this.phoneNumber = next;
        }
      }
    },
    // revalidate when country changes so mask/validator refreshes
    phoneCountrySelected() {
      if (this.$v) {
        this.$v.$touch();
      }
    },
    '$v.phoneNumber.$invalid'(inv) {
      this.$emit('valid-change', !inv);
    },

    // legacy hydration
    responses() {
      if (!this.formName) {
        return;
      }
      const raw = this.responses?.[this.formName]?.[this.inputData.id];
      if (!raw) {
        return;
      }
      const match = String(raw).match(/^\+(\d{1,3})(\d+)$/);
      if (match) {
        const pref = '+' + match[1];
        const local = match[2];
        const found = this.countries.find(country => country.prefix === pref);
        if (found) {
          this.phoneCountry = found;
        }
        this.$v.phoneNumber.$model = local;
      }
    }
  },
  created() {
    this.$eventHub.$on(`validateInput_${this.inputData.id}_${this.stepId}`, () => this.validate());
    this.$eventHub.$on(`resetValidationInput_${this.inputData.id}_${this.stepId}`, () =>
      this.reset()
    );

    if (this.formName && this.responses?.[this.formName]?.[this.inputData.id]) {
      const raw = this.responses[this.formName][this.inputData.id];
      const match = String(raw).match(/^\+(\d{1,3})(\d+)$/);
      if (match) {
        const pref = '+' + match[1];
        const local = match[2];
        const found = this.countries.find(country => country.prefix === pref);
        if (found) {
          this.phoneCountry = found;
        }
        this.$v.phoneNumber.$model = local;
      }
    }

    this.reset();
  },
  beforeDestroy() {
    this.$eventHub.$off(`validateInput_${this.inputData.id}_${this.stepId}`);
    this.$eventHub.$off(`resetValidationInput_${this.inputData.id}_${this.stepId}`);
  },
  methods: {
    validate() {
      this.$v.$touch();
      if (this.formName) {
        this.$store.commit('dynamicForms/updateValidation', {
          id: this.inputData.id,
          value: !this.$v.$invalid,
          formName: this.formName
        });
      }
      this.$emit('valid-change', !this.$v.$invalid);
    },
    reset() {
      this.$v.$reset();
    },
    updateValue(emit = true) {
      this.validate();
      const newValue = normalize(this.phoneCountry?.prefix, this.$v.phoneNumber.$model);

      if (this.formName) {
        this.$store.dispatch('dynamicForms/updateResponse', {
          id: this.inputData.id,
          value: newValue,
          formName: this.formName
        });
      }

      if (emit) {
        this.$emit('update:value', newValue);
      }
    },
    getFlagImage(item) {
      return (item?.flagKey || item?.code || '').toLowerCase();
    }
  }
};
</script>

